import React from 'react';
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  InputAdornment,
} from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { TextField } from '../text-field';

export interface AutocompleteOption<TValue = string> {
  label: string;
  value: TValue;
  meta?: unknown;
}

export interface CustomAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends Omit<
    MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    'renderInput'
  > {
  label?: React.ReactNode;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  autocompleteId?: string;
  icon?: React.ReactNode;
}

export const Autocomplete = <
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(
  props: CustomAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
) => {
  const {
    label,
    placeholder,
    error,
    helperText,
    autocompleteId,
    icon,
    ...rest
  } = props;
  return (
    <MuiAutocomplete
      {...rest}
      id={autocompleteId}
      popupIcon={
        <SystemIcon iconName={'CaretDown'} size={14} color="inherit" />
      }
      getOptionLabel={(option) =>
        props.getOptionLabel
          ? props.getOptionLabel(option)
          : typeof option === 'string'
            ? option
            : ((option as AutocompleteOption)?.label ?? '')
      }
      renderInput={(params) => {
        const { InputLabelProps, InputProps, inputProps, ...other } = params;
        return (
          <TextField
            {...other}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            slotProps={{
              inputLabel: InputLabelProps,
              htmlInput: inputProps,
              input: {
                ...InputProps,
                startAdornment: (
                  <>
                    {icon && (
                      <InputAdornment position="start">{icon}</InputAdornment>
                    )}
                    {InputProps.startAdornment}
                  </>
                ),
              },
            }}
          />
        );
      }}
    />
  );
};

export default Autocomplete;
